/**
 * AdminIntegrationsV2 - Integrations management with Tailwind/shadcn UI.
 * Same logic as SyncIntegrationsModal, redesigned as inline card.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Loader2, Check, Link2, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { ArcaContext } from '../../context/ArcaContext.context';
import { getHoldedApiKey } from '../../api/thirdparty/holded/getHoldedApiKey';
import getActualToken from '../../api/thirdparty/payfit/getActualToken';
import { saveHoldedApiKey } from '../../api/thirdparty/holded/saveHoldedApiKey';
import PayfitFileModalV2 from '../Modals/PayfitFileModalV2';

const AdminIntegrationsV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId } = useContext(ArcaContext);

	const [loading, setLoading] = useState(true);
	const [holdedApiKey, setHoldedApiKey] = useState('');
	const [holdedConnected, setHoldedConnected] = useState(false);
	const [payfitConnected, setPayfitConnected] = useState(false);
	const [showHoldedInput, setShowHoldedInput] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState('');
	const [saved, setSaved] = useState(false);
	const [isPayfitModalOpen, setIsPayfitModalOpen] = useState(false);

	useEffect(() => {
		const fetchIntegrations = async () => {
			if (!token || !companyId) return;
			setLoading(true);
			try {
				const [payfitRes, holdedRes] = await Promise.all([
					getActualToken(token, companyId),
					getHoldedApiKey(token, companyId)
				]);

				if (payfitRes.success && typeof payfitRes.data === 'object' && payfitRes.data.accessToken) {
					setPayfitConnected(true);
				}

				if (holdedRes.success && holdedRes.data?.holdedApiKey) {
					setHoldedApiKey(holdedRes.data.holdedApiKey);
					setHoldedConnected(true);
				}
			} catch (e) {
				console.error('Error fetching integrations:', e);
			} finally {
				setLoading(false);
			}
		};

		fetchIntegrations();
	}, [token, companyId]);

	const handlePayfitModalClose = async () => {
		setIsPayfitModalOpen(false);
		if (token && companyId) {
			const res = await getActualToken(token, companyId);
			setPayfitConnected(res.success && typeof res.data === 'object' && !!res.data.accessToken);
		}
	};

	const handleSaveHoldedApiKey = async () => {
		if (!holdedApiKey) {
			setError(translate('admin.sync.holded.apiKeyRequired'));
			return;
		}

		setIsSaving(true);
		setError('');
		setSaved(false);

		try {
			await saveHoldedApiKey(token, holdedApiKey);
			const res = await getHoldedApiKey(token, companyId);
			if (res.success && res.data?.holdedApiKey) {
				setHoldedConnected(true);
				setHoldedApiKey(res.data.holdedApiKey);
				setSaved(true);
				setShowHoldedInput(false);
				setTimeout(() => setSaved(false), 3000);
			}
		} catch (err) {
			setError(translate('admin.sync.holded.apiKeyError'));
		} finally {
			setIsSaving(false);
		}
	};

	if (loading) {
		return (
			<Card className="border-none shadow-xs">
				<CardContent className="p-6 flex items-center justify-center">
					<Loader2 className="w-6 h-6 animate-spin text-primary" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="border-none shadow-xs">
			<CardContent className="p-6">
				<div className="flex items-start gap-3 mb-6">
					<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
						<Link2 className="w-5 h-5 text-primary" />
					</div>
					<div>
						<h2 className="text-lg font-semibold text-foreground">
							{translate('admin.integrations.available')}
						</h2>
						<p className="text-sm text-muted-foreground mt-0.5">
							{translate('admin.sync.modal.description')}
						</p>
					</div>
				</div>

				{saved && (
					<div className="mb-4 p-3 rounded-lg bg-primary/10 flex items-center gap-2">
						<Check className="w-4 h-4 text-primary" />
						<span className="text-sm text-primary">
							{translate('admin.sync.holded.saveSuccess')}
						</span>
					</div>
				)}

				<div className="space-y-4">
					{/* Regla de Proa «Filas flex con texto» (Foundations/Responsive): flex-wrap, texto min-w-0 y
					    acción shrink-0. En el móvil la acción baja de línea; sin eso, la etiqueta pisaba el texto. */}
					{/* Holded Integration */}
					<div className="rounded-lg border border-border p-4">
						<div className="flex items-center justify-between gap-4 flex-wrap mb-3">
							<div className="flex items-center gap-3 min-w-0">
								<img src="/imgs/holded.png" alt="Holded" className="w-8 h-8 object-contain" />
								<div className="min-w-0">
									<p className="font-medium text-foreground">Holded</p>
									<p className="text-xs text-muted-foreground">
										{translate('admin.integrations.holdedDescription')}
									</p>
								</div>
							</div>
							{holdedConnected ? (
								<Badge className="bg-primary/10 text-primary border-primary/20 gap-1 shrink-0">
									<CheckCircle2 className="w-3 h-3" />
									{translate('admin.sync.holded.connected')}
								</Badge>
							) : (
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowHoldedInput(!showHoldedInput)}
									className="rounded-full shrink-0"
								>
									{translate('admin.integrations.configure')}
								</Button>
							)}
						</div>

						{showHoldedInput && !holdedConnected && (
							<div className="mt-4 p-4 rounded-lg bg-muted/30">
								<div className="space-y-3">
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('admin.sync.holded.apiKeyLabel')}
										</Label>
										<Input
											value={holdedApiKey}
											onChange={(e) => {
												setHoldedApiKey(e.target.value);
												if (error) setError('');
											}}
											placeholder={translate('admin.sync.holded.apiKeyPlaceholder')}
											className={error ? 'border-destructive' : ''}
										/>
										{error && (
											<p className="text-xs text-destructive mt-1 flex items-center gap-1">
												<AlertCircle className="w-3 h-3" />
												{error}
											</p>
										)}
									</div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.sync.holded.guideText')}{' '}
										<a
											href="https://app.holded.com/login?url_after_login=%2Fapi"
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary hover:underline inline-flex items-center gap-1"
										>
											{translate('admin.sync.holded.goToHolded')}
											<ExternalLink className="w-3 h-3" />
										</a>
									</p>
									<Button
										onClick={handleSaveHoldedApiKey}
										disabled={isSaving}
										className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
									>
										{isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
										{translate('admin.sync.holded.save')}
									</Button>
								</div>
							</div>
						)}
					</div>

					{/* Payfit Integration */}
					<div className="rounded-lg border border-border p-4">
						<div className="flex items-center justify-between gap-4 flex-wrap">
							<div className="flex items-center gap-3 min-w-0">
								<img src="/imgs/payfit.png" alt="Payfit" className="w-8 h-8 object-contain" />
								<div className="min-w-0">
									<p className="font-medium text-foreground">Payfit</p>
									<p className="text-xs text-muted-foreground">
										{translate('admin.integrations.payfitDescription')}
									</p>
								</div>
							</div>
							{payfitConnected ? (
								<div className="flex items-center gap-2 shrink-0">
									<Badge className="bg-primary/10 text-primary border-primary/20 gap-1">
										<CheckCircle2 className="w-3 h-3" />
										{translate('admin.sync.payfit.connected')}
									</Badge>
									<Button
										variant="outline"
										size="sm"
										className="rounded-full"
										onClick={() => setIsPayfitModalOpen(true)}
									>
										{translate('admin.integrations.manage')}
									</Button>
								</div>
							) : (
								<Button
									variant="outline"
									size="sm"
									className="rounded-full shrink-0"
									onClick={() => setIsPayfitModalOpen(true)}
								>
									{translate('admin.integrations.configure')}
								</Button>
							)}
						</div>
					</div>

					{/* Other Software */}
					<div className="rounded-lg border border-border p-4">
						<div className="flex items-center justify-between gap-4 flex-wrap">
							<div className="flex items-center gap-3 min-w-0">
								<div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
									<Link2 className="w-4 h-4 text-muted-foreground" />
								</div>
								<div className="min-w-0">
									<p className="font-medium text-foreground">
										{translate('admin.sync.otherSoftware')}
									</p>
									<p className="text-xs text-muted-foreground">
										{translate('admin.integrations.otherDescription')}
									</p>
								</div>
							</div>
							<Button variant="outline" size="sm" className="rounded-full shrink-0">
								{translate('admin.integrations.contact')}
							</Button>
						</div>
					</div>
				</div>
			</CardContent>
			<PayfitFileModalV2 isOpen={isPayfitModalOpen} onClose={handlePayfitModalClose} />
		</Card>
	);
};

export default AdminIntegrationsV2;
